"use client";

import { X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { renderDiscoverNoteSource } from "@/discover/sources/client/discover-client-source-registry";
import { groupBriefsByCategory } from "../../_lib/brief-grouping";
import { useExploreRoute } from "../../_lib/explore-route-context";
import type { ExploreBriefCard, ExploreBriefEvidenceRef } from "../../types";
import { type BriefSurface, usePlayModeTracking } from "../use-brief-analytics";
import {
  findActiveBriefIndex,
  getBriefScrollTop,
} from "./briefs-stories-vertical-overlay-helpers";

function ignoreNoteOpenChange() {
  /* no-op: evidence embeds don't use open state */
}

function NoteSourceEmbed({
  exploreBase,
  note,
}: {
  exploreBase: string;
  note: ExploreBriefEvidenceRef;
}) {
  return renderDiscoverNoteSource({
    exploreBase,
    note,
    onOpenChange: ignoreNoteOpenChange,
  });
}

// ─── Brief card ──────────────────────────────────────────────

function BriefCard({
  brief,
  indexInCategory,
  exploreBase,
}: {
  brief: ExploreBriefCard;
  indexInCategory: number;
  exploreBase: string;
}) {
  const evidence =
    brief.evidence.length > 0
      ? brief.evidence
      : [{ sourceType: brief.sourceType, sourceId: brief.sourceId }];

  return (
    <div
      className="w-full max-w-[500px] rounded-[14px] bg-[var(--explore-bg-card)] p-5 max-sm:max-w-full max-sm:p-4"
      style={{
        fontFamily: "var(--homepage-sans-stack)",
        boxShadow: "var(--explore-inline-shadow)",
      }}
    >
      <div className="mb-2.5 flex items-center gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--explore-bg-highlight)] font-bold text-[0.65rem] text-[var(--explore-text-muted)]">
          {indexInCategory + 1}
        </span>
        {brief.categoryDisplayName && (
          <>
            <span className="text-[0.78rem]">{brief.categoryEmoji}</span>
            <span className="font-medium text-[0.72rem] text-[var(--explore-text-muted)]">
              {brief.categoryDisplayName}
            </span>
          </>
        )}
        <span className="ml-auto text-[0.62rem] text-[var(--explore-text-muted)]">
          {brief.timeAgo}
        </span>
      </div>
      <h3
        className="mb-2.5 font-semibold text-[1.05rem] text-[var(--explore-text-primary)] leading-[1.35]"
        style={{ fontFamily: "var(--explore-card-title-stack)" }}
      >
        {brief.title}
      </h3>
      {brief.tldr && (
        <p
          className="mb-2.5 rounded-lg bg-[var(--explore-bg-tldr)] px-4 py-3 text-[0.85rem] text-[var(--explore-text-secondary)] leading-[1.6]"
          style={{ fontFamily: "var(--reader-body-font-stack)" }}
        >
          {brief.tldr}
        </p>
      )}
      <div className="flex flex-col gap-2 overflow-hidden">
        {evidence.map((ev, i) => (
          <div
            className="min-w-0 max-w-full overflow-hidden"
            key={`${ev.sourceType}:${ev.sourceId}-${i}`}
          >
            <NoteSourceEmbed exploreBase={exploreBase} note={ev} />
          </div>
        ))}
      </div>
      {brief.tags.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {brief.tags.map((tag) => (
            <span
              className="rounded-full bg-[var(--explore-tag-bg)] px-2.5 py-1 font-medium text-[0.72rem] text-[var(--explore-tag-text)]"
              key={tag.slug}
            >
              {tag.displayName}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Per-category brief progress bar ─────────────────────────

function BriefProgressBar({
  totalInCategory,
  currentInCategory,
}: {
  totalInCategory: number;
  currentInCategory: number;
}) {
  if (totalInCategory <= 0) {
    return null;
  }

  return (
    <div className="flex w-full gap-px px-1">
      {Array.from({ length: totalInCategory }).map((_, i) => (
        <div
          className="h-[2px] flex-1 rounded-full"
          key={i}
          style={{
            background:
              i <= currentInCategory
                ? "rgba(255,255,255,0.85)"
                : "rgba(255,255,255,0.15)",
            transition: "background 0.2s ease",
          }}
        />
      ))}
    </div>
  );
}

// ─── Pagination dot color helper ─────────────────────────────

function getPaginationDotBg({
  i,
  current,
}: {
  i: number;
  current: number;
}): string {
  if (i === current) {
    return "#fff";
  }
  if (i <= current) {
    return "rgba(255,255,255,0.5)";
  }
  return "rgba(255,255,255,0.2)";
}

// ─── Pagination track (desktop) ──────────────────────────────

function PaginationTrack({
  count,
  current,
}: {
  count: number;
  current: number;
}) {
  if (count <= 1) {
    return null;
  }
  return (
    <div className="-translate-y-1/2 fixed top-1/2 right-4 z-30 hidden flex-col gap-[3px] sm:flex">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            width: i === current ? 4 : 3,
            height: i === current ? 14 : 6,
            borderRadius: 2,
            background: getPaginationDotBg({ i, current }),
            transition: "all 0.2s ease",
          }}
        />
      ))}
    </div>
  );
}

// ─── Chip background helper ──────────────────────────────────

function getChipBackground({
  isActive,
  isDone,
}: {
  isActive: boolean;
  isDone: boolean;
}): string {
  if (isActive) {
    return "#ffffff";
  }
  if (isDone) {
    return "var(--explore-accent-yellow)";
  }
  return "rgba(255,255,255,0.12)";
}

function getChipColor({
  isActive,
  isDone,
}: {
  isActive: boolean;
  isDone: boolean;
}): string {
  if (isActive) {
    return "#1a1a1a";
  }
  if (isDone) {
    return "#1a1a1a";
  }
  return "#ffffff";
}

// ─── Category chip keyframes ────────────────────────────────────

function CategoryChipKeyframes() {
  return (
    <style>{`
      @keyframes cat-chip-pop {
        0% { transform: scale(1); }
        40% { transform: scale(1.12); }
        70% { transform: scale(0.97); }
        100% { transform: scale(1); }
      }
      @keyframes cat-chip-confetti {
        0% { transform: translate(0, 0) scale(1); opacity: 1; }
        100% { opacity: 0; }
      }
      @keyframes cat-chip-check-draw {
        0% { stroke-dashoffset: 20; }
        100% { stroke-dashoffset: 0; }
      }
    `}</style>
  );
}

const CHIP_CONFETTI_COLORS = [
  "#f59e0b",
  "#fbbf24",
  "#22c55e",
  "#fde68a",
  "#d97706",
  "#86efac",
];

function ChipConfettiParticles() {
  return (
    <>
      {CHIP_CONFETTI_COLORS.map((color, i) => {
        const angle = (i / CHIP_CONFETTI_COLORS.length) * 360;
        const rad = (angle * Math.PI) / 180;
        const tx = Math.cos(rad) * 36;
        const ty = Math.sin(rad) * 36;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: color,
              marginLeft: -3,
              marginTop: -3,
              animation:
                "cat-chip-confetti 600ms cubic-bezier(0.23, 1, 0.32, 1) forwards",
              transform: `translate(${tx}px, ${ty}px) scale(0.5)`,
              animationDelay: `${i * 30}ms`,
            }}
          />
        );
      })}
    </>
  );
}

// ─── Build indexed brief list per category ───────────────────

interface IndexedBrief {
  brief: ExploreBriefCard;
  globalIndex: number;
  indexInCategory: number;
  categorySlug: string;
}

function buildIndexedBriefs({
  groups,
}: {
  groups: ReturnType<typeof groupBriefsByCategory>;
}): IndexedBrief[] {
  const result: IndexedBrief[] = [];
  let globalIdx = 0;
  for (const group of groups) {
    for (let i = 0; i < group.briefs.length; i++) {
      result.push({
        brief: group.briefs[i],
        globalIndex: globalIdx,
        indexInCategory: i,
        categorySlug: group.categorySlug,
      });
      globalIdx++;
    }
  }
  return result;
}

// ─── Export ──────────────────────────────────────────────────

interface Props {
  briefs: ExploreBriefCard[];
  onClose: () => void;
  onAllViewed?: () => void;
  /**
   * Surface that opened the overlay. Threaded into all
   * brief_play_mode_* events as `from_surface` so analytics can
   * compare engagement by entry point. Defaults to the briefs section.
   */
  fromSurface?: BriefSurface;
}

export function BriefStoriesVerticalOverlay({
  briefs,
  onClose,
  onAllViewed,
  fromSurface = "explore_briefs_section",
}: Props) {
  const [mounted, setMounted] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [viewedCount, setViewedCount] = useState(0);
  const [currentGlobalIndex, setCurrentGlobalIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const transitionTimerRef = useRef<number | null>(null);
  const { exploreBase, reportType: edition } = useExploreRoute();
  const { trackStarted, trackAdvanced, trackCompleted, trackDismissed } =
    usePlayModeTracking({
      edition,
      fromSurface,
    });
  const startedAtRef = useRef<number | null>(null);
  const completedRef = useRef(false);
  const prevGlobalIndexRef = useRef(0);
  const dismissMethodRef = useRef<"close_button" | "esc" | "backdrop">(
    "close_button"
  );
  // Source of the most recent index change. Each user input path
  // (chip tap, keyboard arrow) sets this *before* calling
  // `navigateToIndex`. If nothing sets it the change came from a scroll /
  // swipe gesture — the default for this overlay.
  const viaRef = useRef<"auto" | "tap" | "keyboard" | "swipe">("swipe");

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only; fires started once and dismissed on unmount
  useEffect(() => {
    setMounted(true);
    startedAtRef.current = performance.now();
    trackStarted({ totalBriefs: briefs.length });
    return () => {
      if (!completedRef.current) {
        const elapsed = startedAtRef.current
          ? Math.round(performance.now() - startedAtRef.current)
          : 0;
        trackDismissed({
          position: prevGlobalIndexRef.current,
          total: briefs.length,
          dismissMethod: dismissMethodRef.current,
          timeOnPlayModeMs: elapsed,
        });
      }
    };
  }, []);

  const groups = useMemo(() => groupBriefsByCategory({ briefs }), [briefs]);
  const indexedBriefs = useMemo(() => buildIndexedBriefs({ groups }), [groups]);

  useEffect(() => {
    if (groups.length > 0 && !activeCategory) {
      setActiveCategory(groups[0].categorySlug);
    }
  }, [groups, activeCategory]);

  const syncActiveFromScroll = useCallback(
    ({ scrollTop }: { scrollTop: number }) => {
      if (indexedBriefs.length === 0) {
        return;
      }

      const cardOffsetTops = indexedBriefs.map(
        (_brief, index) =>
          cardRefs.current.get(index)?.offsetTop ?? Number.POSITIVE_INFINITY
      );
      const nextIndex = findActiveBriefIndex({
        cardOffsetTops,
        scrollTop,
      });
      const activeBrief = indexedBriefs[nextIndex];
      if (!activeBrief) {
        return;
      }

      setCurrentGlobalIndex(nextIndex);
      setActiveCategory(activeBrief.categorySlug);
      setViewedCount((prev) => Math.max(prev, nextIndex + 1));
    },
    [indexedBriefs]
  );

  const navigateToIndex = useCallback(
    ({ behavior, index }: { behavior: ScrollBehavior; index: number }) => {
      const scrollContainer = scrollRef.current;
      const card = cardRefs.current.get(index);
      const briefAtIndex = indexedBriefs[index];
      if (!(scrollContainer && card && briefAtIndex)) {
        return;
      }

      if (transitionTimerRef.current !== null) {
        window.clearTimeout(transitionTimerRef.current);
      }

      setIsTransitioning(true);
      currentIndexRef.current = index;
      setCurrentGlobalIndex(index);
      setActiveCategory(briefAtIndex.categorySlug);
      setViewedCount((prev) => Math.max(prev, index + 1));

      scrollContainer.scrollTo({
        behavior,
        top: getBriefScrollTop({
          cardOffsetTop: card.offsetTop,
        }),
      });

      transitionTimerRef.current = window.setTimeout(
        () => {
          setIsTransitioning(false);
          transitionTimerRef.current = null;
        },
        behavior === "smooth" ? 220 : 120
      );
    },
    [indexedBriefs]
  );

  // Track the active card from the container scroll position instead of
  // intersection heuristics, which can lag one card behind near boundaries.
  useEffect(() => {
    const el = scrollRef.current;
    if (!(el && mounted)) {
      return;
    }

    const handleScroll = () => {
      syncActiveFromScroll({ scrollTop: el.scrollTop });
    };

    handleScroll();
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [mounted, syncActiveFromScroll]);

  useEffect(() => {
    return () => {
      if (transitionTimerRef.current !== null) {
        window.clearTimeout(transitionTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (viewedCount >= briefs.length && briefs.length > 0) {
      if (!completedRef.current) {
        completedRef.current = true;
        const elapsed = startedAtRef.current
          ? Math.round(performance.now() - startedAtRef.current)
          : 0;
        trackCompleted({ total: briefs.length, timeOnPlayModeMs: elapsed });
      }
      onAllViewed?.();
      // Auto-close after last category animation plays (800ms anim + 600ms pause)
      const timer = setTimeout(() => {
        onClose();
      }, 1400);
      return () => clearTimeout(timer);
    }
  }, [viewedCount, briefs.length, onAllViewed, onClose, trackCompleted]);

  useEffect(() => {
    if (currentGlobalIndex === prevGlobalIndexRef.current) {
      return;
    }
    const brief = briefs[currentGlobalIndex];
    if (brief) {
      trackAdvanced({
        brief,
        meta: {
          position: currentGlobalIndex,
          total: briefs.length,
          via: viaRef.current,
        },
      });
    }
    prevGlobalIndexRef.current = currentGlobalIndex;
    viaRef.current = "swipe";
  }, [currentGlobalIndex, briefs, trackAdvanced]);

  // Category info
  const activeCatInfo = useMemo(() => {
    const group = groups.find((g) => g.categorySlug === activeCategory);
    if (!group) {
      return { count: 0, indexInCat: 0 };
    }
    const ib = indexedBriefs[currentGlobalIndex];
    const indexInCat =
      ib?.categorySlug === activeCategory ? ib.indexInCategory : 0;
    return { count: group.briefs.length, indexInCat };
  }, [activeCategory, groups, indexedBriefs, currentGlobalIndex]);

  const allViewed = viewedCount >= briefs.length && briefs.length > 0;

  const completedCategories = useMemo(() => {
    const set = new Set<string>();
    const activeIdx = groups.findIndex(
      (g) => g.categorySlug === activeCategory
    );
    // All categories before the active one are completed
    for (let i = 0; i < activeIdx; i++) {
      set.add(groups[i].categorySlug);
    }
    // If all briefs are viewed, mark the active (last) category too
    if (allViewed && activeCategory) {
      set.add(activeCategory);
    }
    return set;
  }, [groups, activeCategory, allViewed]);

  // Track which categories just completed for animation
  const [animatingCategories, setAnimatingCategories] = useState<Set<string>>(
    new Set()
  );
  const prevCompleted = useRef<Set<string>>(new Set());
  useEffect(() => {
    const newlyCompleted: string[] = [];
    for (const slug of completedCategories) {
      if (!prevCompleted.current.has(slug)) {
        newlyCompleted.push(slug);
      }
    }
    if (newlyCompleted.length > 0) {
      setAnimatingCategories((prev) => {
        const next = new Set(prev);
        for (const slug of newlyCompleted) {
          next.add(slug);
        }
        return next;
      });
      const timer = setTimeout(() => {
        setAnimatingCategories((prev) => {
          const next = new Set(prev);
          for (const slug of newlyCompleted) {
            next.delete(slug);
          }
          return next;
        });
      }, 800);
      prevCompleted.current = new Set(completedCategories);
      return () => clearTimeout(timer);
    }
    prevCompleted.current = new Set(completedCategories);
  }, [completedCategories]);

  // Category click
  const scrollToCategory = useCallback(
    (slug: string) => {
      const ib = indexedBriefs.find((b) => b.categorySlug === slug);
      if (!ib) {
        return;
      }
      viaRef.current = "tap";
      navigateToIndex({ behavior: "smooth", index: ib.globalIndex });
    },
    [indexedBriefs, navigateToIndex]
  );

  // Refs for keyboard handler to read latest state without re-attaching
  const currentIndexRef = useRef(currentGlobalIndex);
  currentIndexRef.current = currentGlobalIndex;
  const indexedBriefsRef = useRef(indexedBriefs);
  indexedBriefsRef.current = indexedBriefs;

  // Lock body + forward wheel + keyboard (capture-phase on document)
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflow = "hidden";

    // Capture-phase listener — fires regardless of focus, before any
    // other handler. Works even when focus is in an iframe or scroll area.
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        dismissMethodRef.current = "esc";
        onClose();
        return;
      }
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") {
        return;
      }
      e.preventDefault();
      e.stopPropagation();

      const idx = currentIndexRef.current;
      const indexedItems = indexedBriefsRef.current;
      const nextIdx =
        e.key === "ArrowUp"
          ? Math.max(0, idx - 1)
          : Math.min(indexedItems.length - 1, idx + 1);
      if (nextIdx !== idx) {
        viaRef.current = "keyboard";
        navigateToIndex({ behavior: "auto", index: nextIdx });
      }
    };

    const handleWheel = (e: WheelEvent) => {
      if (!scrollRef.current?.contains(e.target as Node)) {
        e.preventDefault();
        scrollRef.current?.scrollBy({ top: e.deltaY });
      }
    };

    document.addEventListener("keydown", handleKey, true);
    document.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
      document.removeEventListener("keydown", handleKey, true);
      document.removeEventListener("wheel", handleWheel);
    };
  }, [navigateToIndex, onClose]);

  if (!mounted) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex flex-col bg-black/50 backdrop-blur-[8px]">
      {/* Desktop side close zones */}
      <div
        className="absolute top-0 bottom-0 left-0 z-10 hidden cursor-pointer sm:block"
        onClick={() => {
          dismissMethodRef.current = "backdrop";
          onClose();
        }}
        role="presentation"
        style={{ width: "calc(50% - 270px)", minWidth: 24 }}
      />
      <div
        className="absolute top-0 right-0 bottom-0 z-10 hidden cursor-pointer sm:block"
        onClick={() => {
          dismissMethodRef.current = "backdrop";
          onClose();
        }}
        role="presentation"
        style={{ width: "calc(50% - 270px)", minWidth: 24 }}
      />

      {/* Header */}
      <div
        className="relative z-20 flex flex-col gap-2 px-4 pb-1 sm:px-5"
        style={{
          paddingTop: "max(0.75rem, env(safe-area-inset-top, 0.75rem))",
        }}
      >
        {/* Counter: categories progress + close */}
        <div className="flex items-center justify-between">
          <span className="font-semibold text-[0.72rem] text-white/60 tabular-nums">
            {groups.findIndex((g) => g.categorySlug === activeCategory) + 1} of{" "}
            {groups.length} categories
          </span>
          <button
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white/90"
            onClick={onClose}
            type="button"
          >
            <X size={18} />
          </button>
        </div>

        {/* Category chips — progress bar under active chip only */}
        <CategoryChipKeyframes />
        <div className="scrollbar-none flex gap-2 overflow-x-auto sm:justify-center">
          {groups.map((group) => {
            const isActive = group.categorySlug === activeCategory;
            const isDone = completedCategories.has(group.categorySlug);
            const isAnimating = animatingCategories.has(group.categorySlug);
            return (
              <div
                className="flex shrink-0 flex-col items-center gap-1"
                key={group.categorySlug}
              >
                <div className="relative">
                  {isAnimating && <ChipConfettiParticles />}
                  <button
                    className="flex items-center gap-1.5 rounded-full border-none px-3 py-1.5 text-[0.72rem]"
                    onClick={() => scrollToCategory(group.categorySlug)}
                    style={{
                      fontFamily: "var(--homepage-sans-stack)",
                      fontWeight: isActive || isDone ? 700 : 500,
                      background: getChipBackground({ isActive, isDone }),
                      color: getChipColor({ isActive, isDone }),
                      cursor: "pointer",
                      transition:
                        "background 300ms cubic-bezier(0.23, 1, 0.32, 1), color 200ms cubic-bezier(0.23, 1, 0.32, 1)",
                      animation: isAnimating
                        ? "cat-chip-pop 500ms cubic-bezier(0.23, 1, 0.32, 1)"
                        : "none",
                    }}
                    type="button"
                  >
                    <span>{group.categoryEmoji}</span>
                    <span>{group.categoryDisplayName}</span>
                    <span className="font-bold">
                      {isDone ? (
                        <svg
                          className="h-3 w-3"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={3}
                          style={{
                            strokeDasharray: 20,
                            animation: isAnimating
                              ? "cat-chip-check-draw 400ms cubic-bezier(0.23, 1, 0.32, 1)"
                              : "none",
                          }}
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M5 13l4 4L19 7"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        group.briefs.length
                      )}
                    </span>
                  </button>
                </div>
                {/* Progress bar — only under active category */}
                {isActive && (
                  <BriefProgressBar
                    currentInCategory={activeCatInfo.indexInCat}
                    totalInCategory={activeCatInfo.count}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Scrollable cards — NO divider cards */}
      <div
        className="relative z-5 flex flex-1 flex-col items-center gap-4 overflow-y-auto px-4 pt-3 sm:px-4"
        ref={scrollRef}
        style={{
          paddingBottom: "max(4rem, env(safe-area-inset-bottom, 4rem))",
          opacity: isTransitioning ? 0.3 : 1,
          transition: "opacity 0.15s ease",
        }}
      >
        {indexedBriefs.map((ib) => (
          <div
            data-category={ib.categorySlug}
            data-index={ib.globalIndex}
            key={ib.brief.briefId}
            ref={(el) => {
              if (el) {
                cardRefs.current.set(ib.globalIndex, el);
                return;
              }
              cardRefs.current.delete(ib.globalIndex);
            }}
          >
            <BriefCard
              brief={ib.brief}
              exploreBase={exploreBase}
              indexInCategory={ib.indexInCategory}
            />
          </div>
        ))}
      </div>

      {/* Desktop pagination track */}
      <PaginationTrack
        count={activeCatInfo.count}
        current={activeCatInfo.indexInCat}
      />
    </div>,
    document.body
  );
}
